import React, {useState} from "react";
import useApiCall from "../hooks/CancellableApiCall";
import {PayeeResponse} from "../data/PayeeResponse";
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    InputLabel,
    FormControl,
    ListSubheader
} from "@mui/material";
import {PoliceUser} from "../components/PoliceUser";
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Per-user: set of roleIds excluded from bonus
type ExcludedRoles = { [communityId: number]: Set<number> }

const PayeeRow = ({payee, extraPay, setExtraPay, blacklistedIds, setBlacklistIds, excludedRoles, setExcludedRoles}: {
    payee: PayeeResponse,
    extraPay: { [communityId: string]: number },
    setExtraPay: React.Dispatch<React.SetStateAction<{ [communityId: string]: number }>>,
    blacklistedIds: number[],
    setBlacklistIds: React.Dispatch<React.SetStateAction<number[]>>,
    excludedRoles: ExcludedRoles,
    setExcludedRoles: React.Dispatch<React.SetStateAction<ExcludedRoles>>
}) => {
    const [open, setOpen] = useState(false)
    const excluded = excludedRoles[payee.payeeCommunityId] || new Set()
    const activeRoleCount = payee.adminRoles.filter(r => !excluded.has(r.roleId)).length

    // Recalculate bonus based on active roles
    const baseAmount = payee.paycheckType === "paycheck:administrative-role" ? 110000 : payee.paycheckAmount - payee.adminRoleBonus
    const roleBonus = payee.paycheckType === "paycheck:administrative-role" ? Math.max(0, activeRoleCount - 1) * 15000 : activeRoleCount * 15000
    const effectiveAmount = baseAmount + roleBonus

    let payCheckColor
    if (extraPay[payee.payeeCommunityId] > 0) {
        payCheckColor = "success.main"
    } else if (extraPay[payee.payeeCommunityId] < 0) {
        payCheckColor = "error.main"
    } else {
        payCheckColor = "info.main"
    }

    const toggleRole = (roleId: number, checked: boolean) => {
        setExcludedRoles(prev => {
            const current = new Set(prev[payee.payeeCommunityId] || [])
            if (checked) {
                current.delete(roleId)
            } else {
                current.add(roleId)
            }
            return {...prev, [payee.payeeCommunityId]: current}
        })
    }

    return <>
        <TableRow>
            <TableCell padding="checkbox">
                {payee.adminRoles.length > 0 && (
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    </IconButton>
                )}
            </TableCell>
            <TableCell>
                <PoliceUser user={{nick: payee.payeeNick, communityId: payee.payeeCommunityId.toString()}}/>
            </TableCell>
            <TableCell>{payee.bankAccount.split(".")[0]}</TableCell>
            <TableCell>{payee.paycheckType.substring(9).replace("-", " ").replace(/(^\w|\s\w)/g, m => m.toUpperCase())}</TableCell>
            <TableCell>{activeRoleCount} / {payee.adminRoles.length}</TableCell>
            <TableCell>${baseAmount.toLocaleString()}</TableCell>
            <TableCell sx={{color: roleBonus > 0 ? "success.main" : "text.secondary"}}>
                +${roleBonus.toLocaleString()}
            </TableCell>
            <TableCell>${effectiveAmount.toLocaleString()}</TableCell>
            <TableCell>
                <FormControl sx={{m: 1, width: 200}} variant="standard">
                    <InputLabel id="bonus-amount">Bonus Amount</InputLabel>
                    <Select sx={{color: payCheckColor}} id={`${payee.payeeCommunityId}-extra`} defaultValue={0}
                            label="Bonus Amount" onChange={(event) =>
                        setExtraPay(prevState => ({
                            ...prevState,
                            [payee.payeeCommunityId]: event.target.value
                        }))
                    }>
                        <ListSubheader sx={{color: 'info.main'}}>Base</ListSubheader>
                        <MenuItem key={0} value={"0"} sx={{color: 'info.main'}}>$0</MenuItem>
                        <ListSubheader sx={{color: 'error.main'}}>Subtraction</ListSubheader>
                        <MenuItem value={"-15000"} sx={{color: 'error.main'}}>$15,000</MenuItem>
                        <MenuItem value={"-30000"} sx={{color: 'error.main'}}>$30,000</MenuItem>
                        <MenuItem value={"-45000"} sx={{color: 'error.main'}}>$45,000</MenuItem>
                        <MenuItem value={"-50000"} sx={{color: 'error.main'}}>$50,000</MenuItem>
                        <MenuItem value={"-60000"} sx={{color: 'error.main'}}>$60,000</MenuItem>
                        <MenuItem value={"-65000"} sx={{color: 'error.main'}}>$65,000</MenuItem>
                        <ListSubheader sx={{color: 'success.main'}}>Addition</ListSubheader>
                        <MenuItem value={"15000"} sx={{color: 'success.main'}}>$15,000</MenuItem>
                        <MenuItem value={"30000"} sx={{color: 'success.main'}}>$30,000</MenuItem>
                        <MenuItem value={"45000"} sx={{color: 'success.main'}}>$45,000</MenuItem>
                        <MenuItem value={"50000"} sx={{color: 'success.main'}}>$50,000</MenuItem>
                        <MenuItem value={"60000"} sx={{color: 'success.main'}}>$60,000</MenuItem>
                        <MenuItem value={"65000"} sx={{color: 'success.main'}}>$65,000</MenuItem>
                    </Select>
                </FormControl>
            </TableCell>
            <TableCell>
                <FormControl sx={{m: 1, width: 100}} variant="standard">
                    <Select
                        id='select-allowed'
                        value={Number(blacklistedIds.includes(payee.payeeCommunityId))}
                        onChange={(event) => {
                            if (Number(event.target.value) === 1) {
                                setBlacklistIds([...blacklistedIds, payee.payeeCommunityId])
                            } else {
                                setBlacklistIds(blacklistedIds.filter(id => payee.payeeCommunityId !== id))
                            }
                        }}
                    >
                        <MenuItem key={0} value={0}>Yes</MenuItem>
                        <MenuItem key={1} value={1}>No</MenuItem>
                    </Select>
                </FormControl>
            </TableCell>
        </TableRow>
        {payee.adminRoles.length > 0 && (
            <TableRow>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{m: 1}}>
                            <Typography variant="subtitle2" gutterBottom>
                                Administrative Roles ({activeRoleCount} active)
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Include in Bonus</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payee.adminRoles.map(role => (
                                        <TableRow key={role.roleId}>
                                            <TableCell>{role.displayName}</TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={!excluded.has(role.roleId)}
                                                    onChange={(e) => toggleRole(role.roleId, e.target.checked)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        )}
    </>
}

export const PaycheckPage = () => {
    const [blacklistedIds, setBlacklistIds] = useState<number[]>([])
    const [paymentScript, setPaymentScript] = useState<string | null>(null)
    const [extraPay, setExtraPay] = useState<{ [communityId: string]: number }>({})
    const [excludedRoles, setExcludedRoles] = useState<ExcludedRoles>({})
    const loadPayees = useApiCall<PayeeResponse[]>({
        url: `/api/paycheck/`
    })

    const getEffectiveAmount = (payee: PayeeResponse) => {
        const excluded = excludedRoles[payee.payeeCommunityId] || new Set()
        const activeRoleCount = payee.adminRoles.filter(r => !excluded.has(r.roleId)).length
        const baseAmount = payee.paycheckType === "paycheck:administrative-role" ? 110000 : payee.paycheckAmount - payee.adminRoleBonus
        const roleBonus = payee.paycheckType === "paycheck:administrative-role" ? Math.max(0, activeRoleCount - 1) * 15000 : activeRoleCount * 15000
        return baseAmount + roleBonus
    }

    let pageContent
    if (!paymentScript) {
        pageContent = <>
            <Box sx={{overflowX: "auto"}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell/>
                            <TableCell>Nick</TableCell>
                            <TableCell>Bank ID</TableCell>
                            <TableCell>Payment Type</TableCell>
                            <TableCell>Roles</TableCell>
                            <TableCell>Base Amount</TableCell>
                            <TableCell>Role Bonus</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Additional Bonus</TableCell>
                            <TableCell>Pay</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadPayees.data && loadPayees.data.map((payee) => (
                            <PayeeRow
                                key={payee.payeeCommunityId}
                                payee={payee}
                                extraPay={extraPay}
                                setExtraPay={setExtraPay}
                                blacklistedIds={blacklistedIds}
                                setBlacklistIds={setBlacklistIds}
                                excludedRoles={excludedRoles}
                                setExcludedRoles={setExcludedRoles}
                            />
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <Button color="success" onClick={() => {
                let script = `echo "Starting payroll script"\n`
                loadPayees.data?.forEach(payee => {
                    let bonus = 0
                    if (!isNaN(Number(extraPay[payee.payeeCommunityId]))) {
                        bonus = Number(extraPay[payee.payeeCommunityId])
                    }
                    if (!blacklistedIds.includes(payee.payeeCommunityId)) {
                        const amount = getEffectiveAmount(payee) + bonus
                        const bankaccount = payee.bankAccount.split(".")[0]
                        script += `echo "Sending $${amount} to ${payee.payeeNick}"\nscam givemoneybank ${bankaccount} ${amount} "PLPD Paycheck"\n`
                    }
                })
                setPaymentScript(script)
            }}>Generate Script</Button>
        </>
    } else {
        pageContent = <>
            <Typography variant="h6">Payment Script</Typography>
            <Typography variant="subtitle1" sx={{whiteSpace: "pre-wrap", mb: 1}}>{paymentScript}</Typography>
            <Button color="info" variant="text" onClick={() => setPaymentScript(null)}>
                Return
            </Button>
            <Button color="success" variant="text" startIcon={<ContentPasteIcon/>}
                    onClick={() => {navigator.clipboard.writeText(paymentScript)}}>
                Copy to Clipboard
            </Button>
        </>
    }

    return <Paper variant={"outlined"} style={{marginTop: "16px", padding: "16px"}}>
        {pageContent}
    </Paper>
}
